import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Button, Modal, Text } from '@viapeople/components'
import { useEffect, useState } from 'react'
import { useToast } from '../../../utils/ToastContext'
import { trpc } from '../../../utils/trpc'
import type { ManagerRow } from './types'

interface ManagerVisibilityModalProps {
	isOpen: boolean
	onClose: () => void
	cycleId: string
	organizationId: string
	manager: ManagerRow | null
}

const VISIBILITY_OPTIONS: { value: ManagerRow['visibility']; label: string; description: string }[] = [
	{
		value: 'default',
		label: 'Default Visibility',
		description: 'Access to direct reports only',
	},
	{
		value: 'skip',
		label: 'Skip-Level Visibility',
		description: 'Access to direct reports and their direct reports (2 levels deep)',
	},
	{
		value: 'extended',
		label: 'Extended Visibility',
		description: 'Access to the entire downline',
	},
]

export const ManagerVisibilityModal = ({
	isOpen,
	onClose,
	cycleId,
	organizationId,
	manager,
}: ManagerVisibilityModalProps) => {
	const { showToast } = useToast()
	const queryClient = useQueryClient()

	const [visibility, setVisibility] = useState<ManagerRow['visibility']>(manager?.visibility ?? 'default')
	const [enableSearchEvaluations, setEnableSearchEvaluations] = useState<boolean>(
		manager?.enableSearchEvaluations ?? false,
	)
	const [enablePdfViewer, setEnablePdfViewer] = useState<boolean>(manager?.enablePdfViewer ?? true)

	// Reset local state when manager changes
	useEffect(() => {
		setVisibility(manager?.visibility ?? 'default')
		setEnableSearchEvaluations(manager?.enableSearchEvaluations ?? false)
		setEnablePdfViewer(manager?.enablePdfViewer ?? true)
	}, [manager])

	const upsertMutation = useMutation({
		...trpc.admin.cycles.hierarchies.managerVisibility.upsert.mutationOptions({}),
		onSuccess: () => {
			showToast('Manager visibility updated')
			queryClient.invalidateQueries({
				queryKey: trpc.admin.cycles.hierarchies.managerVisibility.list.queryKey({
					cycleId,
					organizationId,
				}),
				exact: false,
				refetchType: 'all',
			})
			onClose()
		},
		onError: () => {
			showToast('Failed to update manager visibility', 'error')
		},
	})

	const handleSave = () => {
		if (!manager) return
		upsertMutation.mutate({
			cycleId,
			managerId: manager.managerId,
			visibility,
			enableSearchEvaluations,
			enablePdfViewer,
		})
	}

	return (
		<Modal isOpen={isOpen} onClose={onClose} title='Configure Manager Visibility' className='min-w-[500px]'>
			{manager && (
				<div className='flex flex-col gap-6 min-w-[400px]'>
					<div>
						<Text variant='small' weight='semibold' className='text-neutral-700'>
							Manager
						</Text>
						<Text variant='small' className='text-neutral-900 mt-1'>
							{manager.firstName} {manager.lastName}
						</Text>
						<Text variant='xsmall' className='text-neutral-500'>
							{manager.email}
						</Text>
					</div>

					<div className='flex flex-col gap-2'>
						<Text variant='small' weight='semibold' className='text-neutral-700'>
							Visibility Scope
						</Text>
						<div className='flex flex-col gap-2'>
							{VISIBILITY_OPTIONS.map((option) => (
								<label
									key={option.value}
									className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
										visibility === option.value
											? 'border-primary-500 bg-primary-50'
											: 'border-neutral-200 bg-white hover:border-neutral-300'
									}`}>
									<input
										type='radio'
										name='visibility'
										value={option.value}
										checked={visibility === option.value}
										onChange={() => setVisibility(option.value)}
										className='mt-0.5 accent-primary-600'
									/>
									<div>
										<Text variant='small' weight='semibold'>
											{option.label}
										</Text>
										<Text variant='xsmall' className='text-neutral-500 mt-0.5'>
											{option.description}
										</Text>
									</div>
								</label>
							))}
						</div>
					</div>

					<div className='flex flex-col gap-2'>
						<Text variant='small' weight='semibold' className='text-neutral-700'>
							Permissions
						</Text>
						<label className='flex items-center gap-3 p-3 rounded-lg border border-neutral-200 bg-white cursor-pointer hover:border-neutral-300 transition-colors'>
							<input
								type='checkbox'
								checked={enableSearchEvaluations}
								onChange={(e) => setEnableSearchEvaluations(e.target.checked)}
								className='accent-primary-600'
							/>
							<div>
								<Text variant='small' weight='semibold'>
									Search Evaluations
								</Text>
								<Text variant='xsmall' className='text-neutral-500 mt-0.5'>
									Allow this manager to search and view evaluations within their visibility scope
								</Text>
							</div>
						</label>
						<label className='flex items-center gap-3 p-3 rounded-lg border border-neutral-200 bg-white cursor-pointer hover:border-neutral-300 transition-colors'>
							<input
								type='checkbox'
								checked={enablePdfViewer}
								onChange={(e) => setEnablePdfViewer(e.target.checked)}
								className='accent-primary-600'
							/>
							<div>
								<Text variant='small' weight='semibold'>
									PDF Viewer
								</Text>
								<Text variant='xsmall' className='text-neutral-500 mt-0.5'>
									Allow this manager to view evaluation PDFs within their visibility scope
								</Text>
							</div>
						</label>
					</div>

					<div className='flex justify-end gap-2 pt-2 border-t border-neutral-100'>
						<Button variant='secondary' onClick={onClose} disabled={upsertMutation.isPending}>
							Cancel
						</Button>
						<Button variant='primary' onClick={handleSave} disabled={upsertMutation.isPending}>
							{upsertMutation.isPending ? 'Saving…' : 'Save'}
						</Button>
					</div>
				</div>
			)}
		</Modal>
	)
}
